/**
 * Client-side error logger.
 *
 * Captures errors that Nuxt/Vue would otherwise swallow when rendering
 * `error.vue` (the "An error occurred" page), so they actually surface in
 * the browser console with a real stack trace.
 *
 * Hooks four sources:
 *  - Vue's component-level errors (render, watcher, lifecycle)
 *  - Nuxt's `vue:error` / `app:error` lifecycle hooks
 *  - Uncaught synchronous errors (`window.error`)
 *  - Unhandled promise rejections (`window.unhandledrejection`)
 */
export default defineNuxtPlugin((nuxtApp) => {
  const log = (source: string, err: unknown, ...extra: unknown[]) => {
    // Use console.error so DevTools' "Errors" filter picks it up.
    // eslint-disable-next-line no-console
    console.error(`[error-logger:${source}]`, err, ...extra)
  }

  nuxtApp.vueApp.config.errorHandler = (err, instance, info) => {
    log('vue', err, { info, component: instance?.$options?.name ?? instance?.$options?.__name })
  }

  nuxtApp.hook('vue:error', (err, _instance, info) => {
    log('vue:hook', err, { info })
  })

  nuxtApp.hook('app:error', (err) => {
    log('app', err)
  })

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      log('window.error', event.error ?? event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    })

    window.addEventListener('unhandledrejection', (event) => {
      log('window.unhandledrejection', event.reason)
    })
  }
})
